import useLandingStore from '../Store/Landing';

export interface BrandData {
  brandName: string;
  logoUrl: string;
  themeColor?: string;
  backgroundColor?: string;
}

class BrandManager {
  private static instance: BrandManager;
  private currentBrand: BrandData | null = null;

  static getInstance(): BrandManager {
    if (!BrandManager.instance) {
      BrandManager.instance = new BrandManager();
    }
    return BrandManager.instance;
  }

  // Update favicon dynamically
  updateFavicon(logoUrl: string): void {
    const link =
      (document.querySelector("link[rel*='icon']") as HTMLLinkElement) ||
      (document.createElement('link') as HTMLLinkElement);
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = logoUrl;

    // Remove existing favicon links
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach((link) => link.remove());

    // Add new favicon
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  // Update page title
  updatePageTitle(brandName: string): void {
    document.title = `${brandName}`;
  }

  // Generate dynamic manifest
  generateManifest(brandData: BrandData): any {
    return {
      short_name: brandData.brandName,
      name: `${brandData.brandName}`,
      icons: [
        {
          src: brandData.logoUrl,
          sizes: '64x64 32x32 24x24 16x16',
          type: 'image/png'
        },
        {
          src: brandData.logoUrl,
          type: 'image/png',
          sizes: '192x192'
        },
        {
          src: brandData.logoUrl,
          type: 'image/png',
          sizes: '512x512'
        }
      ],
      start_url: '.',
      display: 'standalone',
      theme_color: brandData.themeColor || '#000000',
      background_color: brandData.backgroundColor || '#ffffff'
    };
  }

  // Update PWA manifest
  updatePWAmanifest(brandData: BrandData): void {
    const manifest = this.generateManifest(brandData);
    const manifestBlob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json'
    });
    const manifestUrl = URL.createObjectURL(manifestBlob);

    // Update manifest link
    const manifestLink = document.querySelector(
      'link[rel="manifest"]'
    ) as HTMLLinkElement;
    console.log(manifestLink);
    if (manifestLink) {
      manifestLink.href = manifestUrl;
    }
  }

  // Generate brand-specific service worker
  generateServiceWorker(brandData: BrandData): string {
    const swContent = `
      const CACHE_NAME = '${brandData.brandName}-club-link-v1';
      const urlsToCache = [
        '/',
        '/static/js/bundle.js',
        '/static/css/main.css'
      ];
      
      self.addEventListener('install', (event) => {
        event.waitUntil(
          caches.open(CACHE_NAME)
            .then((cache) => cache.addAll(urlsToCache))
        );
      });
      
      self.addEventListener('fetch', (event) => {
        event.respondWith(
          caches.match(event.request)
            .then((response) => {
              return response || fetch(event.request);
            })
        );
      });
    `;

    const blob = new Blob([swContent], { type: 'application/javascript' });
    return URL.createObjectURL(blob);
  }

  // Register brand-specific PWA
  async registerBrandPWA(brandData: BrandData): Promise<void> {
    try {
      // Update favicon
      this.updateFavicon(brandData.logoUrl);

      // Update page title
      this.updatePageTitle(brandData.brandName);

      // Update PWA manifest
      this.updatePWAmanifest(brandData);

      // Register service worker
      if ('serviceWorker' in navigator) {
        const swUrl = this.generateServiceWorker(brandData);
        await navigator.serviceWorker.register(swUrl);
        console.log('Brand-specific PWA registered successfully');
      }

      this.currentBrand = brandData;
    } catch (error) {
      console.error('Error registering brand PWA:', error);
    }
  }

  // Get current brand
  getCurrentBrand(): BrandData | null {
    return this.currentBrand;
  }

  // Initialize brand from landing data
  async initializeBrand(landingData: any): Promise<void> {
    if (landingData?.brandName && landingData?.logoUrl) {
      const brandData: BrandData = {
        brandName: landingData.brandName,
        logoUrl: landingData.logoUrl,
        themeColor: landingData.themeColor,
        backgroundColor: landingData.backgroundColor
      };

      await this.registerBrandPWA(brandData);
    }
  }
}

export default BrandManager.getInstance();
